import { useQueryClient } from "@tanstack/react-query";

import {
  useCancelAppointmentMutation,
  useCreateAppointmentMutation,
  useCreateBlockedTimeMutation,
  useDeleteAppointmentMutation,
  useDeleteBlockedTimeMutation,
  useGetCalendarItemsQuery,
  useUpdateAppointmentMutation,
  useUpdateBlockedTimeMutation,
} from "~/api/scheduling/scheduling.generated";
import { useBookingFetchInterval, useTimeslots } from "~/app/scheduling/agenda/context";

export * from "./scheduling.generated";

const useGetCalendarItemsKeyGenerator = () => {
  const { interval } = useBookingFetchInterval();

  return useGetCalendarItemsQuery.getKey({
    input: {
      from: interval.start.toMillis(),
      to: interval.end.toMillis(),
    },
  });
};

/**
 * Hook for creating a new Appointment.  Handles the invalidation of the calendar items query.
 */
export const useCreateAppointment = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const { timeslotQueryKeys } = useTimeslots();
  const queryClient = useQueryClient();

  return useCreateAppointmentMutation({
    // Fire-and-forget: do NOT await invalidateQueries here.
    // mutateAsync must resolve before the refetch starts so that clearFormSafe()
    // runs before the calendar store is updated, preventing the newly created
    // appointment from briefly appearing as conflicting with itself (NTD-2512).
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: timeslotQueryKeys });
    },
  });
};

/**
 * Hook for updating an Appointment via the "reschedule button".  Handles the invalidation of the
 * calendar items query.
 */
export const useUpdateAppointment = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const { timeslotQueryKeys } = useTimeslots();
  const queryClient = useQueryClient();

  return useUpdateAppointmentMutation({
    // Fire-and-forget: do NOT await invalidateQueries here.
    // mutateAsync must resolve before the refetch starts so that clearFormSafe()
    // runs before the calendar store is updated, preventing the newly created
    // appointment from briefly appearing as conflicting with itself (NTD-2512).
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: timeslotQueryKeys });
    },
  });
};

/**
 * Hook for deleting an Appointment.  Handles the invalidation of the calendar items query.
 */
export const useDeleteAppointment = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const { timeslotQueryKeys } = useTimeslots();
  const queryClient = useQueryClient();

  return useDeleteAppointmentMutation({
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: timeslotQueryKeys });
    },
  });
};

export const useCancelAppointment = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const queryClient = useQueryClient();

  return useCancelAppointmentMutation({
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useCreateBlockedTime = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const queryClient = useQueryClient();

  return useCreateBlockedTimeMutation({
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateBlockedTime = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const queryClient = useQueryClient();

  return useUpdateBlockedTimeMutation({
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useDeleteBlockedTime = () => {
  const queryKey = useGetCalendarItemsKeyGenerator();
  const queryClient = useQueryClient();

  return useDeleteBlockedTimeMutation({
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
};
